/**
 * Single source of truth for every published price in the solution-led messaging
 * marketing pages (`/pricing` and `/proof-of-concept`).
 *
 * Both pages read `MONTHLY_FLOOR` from here so the qualifier's stated monthly
 * floor and the pricing page's subscription figure can never drift apart -
 * `pricing.test.ts` asserts that invariant.
 *
 * All figures are ZAR, excl. VAT, billed monthly unless stated once-off.
 *
 * Decided 2026-08-28 (Allan): the page moved away from fixed tiers (Core /
 * Growth / Scale, with connector and AI-action caps) entirely. What's included
 * now depends on the organisation's goal, not a package. Two headline monthly
 * figures only - subscription and the fractional AI & automation team - plus
 * a once-off implementation figure carried as a plain note rather than a third
 * headline stat, for organisations bringing their own team. This superseded
 * the tiered structure and its `TIERS` / `CORE_TIER` / `NARROWER_SCOPE` /
 * `TOP_UP` / `COMPOUNDING` exports, all removed. The 2026-08-01 rule they
 * enforced - no money, salary, headcount or FTE language framing automation as
 * a replacement for people - carries forward unconditionally into the new
 * copy below.
 */

/** Rand per month, excl. VAT. The universal floor - every engagement needs it. */
export const SUBSCRIPTION_FROM = 10_000;

/**
 * Rand per month, excl. VAT. Larasoft embedded as a fractional AI & automation
 * team, working alongside whichever internal team the organisation already
 * has (or building one, if not). Starts small, scales with ambition.
 */
export const FRACTIONAL_FROM = 30_000;

/**
 * Rand, once-off, excl. VAT. The initial implementation: connecting the
 * organisation's systems and getting data landing correctly. Applies whether
 * or not the fractional team is engaged, but is only surfaced as a headline
 * note for organisations bringing their own team - see `OWN_TEAM_NOTE`.
 *
 * Deliberately does not collide with either monthly figure above -
 * `pricing.test.ts` pins that.
 */
export const IMPLEMENTATION_FROM = 50_000;

/**
 * The monthly floor quoted on the qualifier (`/proof-of-concept`) and the
 * homepage. Deliberately identical to `SUBSCRIPTION_FROM` - see
 * `pricing.test.ts`. Do not hard-code this figure anywhere else.
 */
export const MONTHLY_FLOOR = SUBSCRIPTION_FROM;

/**
 * USD per month, excl. VAT, for organisations outside South Africa. Shown
 * alongside `SUBSCRIPTION_FROM` at equal visual weight on `/pricing`, not as
 * a converted or secondary figure - decided 2026-08-31 (Allan).
 */
export const SUBSCRIPTION_FROM_USD = 800;

/**
 * USD per month, excl. VAT, for organisations outside South Africa. Shown
 * alongside `FRACTIONAL_FROM` at equal visual weight on `/pricing`.
 */
export const FRACTIONAL_FROM_USD = 2_000;

/**
 * Illustrative days-to-outcome figure quoted in the pricing hero ("in as soon
 * as N days"). A single named constant so it is a one-line change per
 * engagement or campaign, not a hunt through JSX. Not a guarantee, framed with
 * "as soon as" in the copy that reads it.
 */
export const DAYS_TO_OUTCOME = 90;

/**
 * The foundation description shared by the pricing hero. Deliberately makes no
 * claim beyond what the platform does - the compounding argument is a
 * separate, adjacent note (`COMPOUNDING_NOTE`) so each can be tested and
 * edited independently.
 */
export const FOUNDATION_COPY =
  "Every engagement runs on the same foundation: systems connected, workflows automated, real answers from real data.";

/**
 * The network-effect argument for why the platform is worth compounding
 * investment in, replacing the old per-tier `COMPOUNDING` block and
 * `AUTOMATION_HOURS_NOTE`, both removed with the fixed tiers they described.
 *
 * The organisation's own usage and feedback are named as the first source of
 * improvement, deliberately, before the shared-engine argument - so the
 * platform getting smarter for everyone never reads as the organisation's own
 * people mattering less. Reviewed 2026-08-28 (Fable) against the 2026-08-01
 * no-money-no-headcount rule below; passes clean.
 *
 * Carries forward the 2026-08-01 rule (Allan): no money, salary, headcount, or
 * FTE language anywhere in this copy. `pricing.test.ts` pins that.
 */
export const COMPOUNDING_NOTE =
  "And that foundation compounds. It sharpens from how you use it and the feedback you give our team, and it improves again every time we sharpen the same engine for our wider client base. You get a compound return: your own progress, and everyone else’s.";

/**
 * The plain-English note covering organisations that already have their own
 * team: they likely only need the once-off implementation, not the ongoing
 * fractional engagement. Carries `IMPLEMENTATION_FROM` so the two can never
 * drift apart - `pricing.test.ts` asserts the figure appears in this string.
 *
 * Superseded an earlier "booster phase" euphemism (rejected, 2026-08-28,
 * Allan: overkill for a brochure) and, before that, an unpriced escape hatch
 * modelled on the old tiered page's `NARROWER_SCOPE`. This version names the
 * real figure plainly instead.
 */
export const OWN_TEAM_NOTE = `If you have your own team, we do the initial implementation from ${formatRand(IMPLEMENTATION_FROM)}, alongside them.`;

/**
 * Formats a rand amount the South African way: R10 000, R50 000.
 *
 * Grouped by hand rather than via `toLocaleString("en-ZA")` - that returns a
 * comma, a plain space or a non-breaking space depending on the runtime's ICU
 * build, which would make both the rendered page and its tests
 * environment-dependent.
 */
export function formatRand(amount: number): string {
  return `R${formatThousands(amount)}`;
}

/**
 * Formats a USD amount the same way as `formatRand`: $800, $2 000. Same
 * hand-rolled grouping via `formatThousands`, for the same reason.
 */
export function formatUsd(amount: number): string {
  return `$${formatThousands(amount)}`;
}

/**
 * Space-grouped thousands, no currency symbol. Same hand-rolled grouping as
 * `formatRand` and for the same reason: `toLocaleString` returns a comma, a
 * plain space or a non-breaking space depending on the runtime's ICU build,
 * which would make the rendered page environment-dependent.
 */
export function formatThousands(value: number): string {
  return Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
